import React, { useReducer, useRef, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import Api from './API';
import { styles } from './styles';
import Search from '../SmartForms/Search';
import GenericTable from '../Components/GenericTable/GenericTable';
import CandidatesTableRows from '../Candidates/CandidatesTableRows';
import { headerStyle } from '../Candidates/config';
import { CandidatesReducer, InitialCandidatesState } from '../Candidates/reducer';
import { ICandidateSourcingSearch, ICandidateSourcingFilters, IApiResponse } from './types';
import Tag from '../Components/Tag/Tag';
import TotalSelectedIndicatorWithActions from '../Components/GenericTable/TotalSelectedIndicatorWithActions';
import DeleteIcon from '@mui/icons-material/Delete';
import { tableSearchButtonStyles, baseSecondaryButtonStyles } from '../shared/commonStyles';
import JobSelectionDialog from './JobSelectionDialog';
import { IJob } from './types';

const filterToLabel = {
  job_titles: 'Job Titles',
  skills: 'Skills',
  industries: 'Industry',
  years_of_experience: 'Years of Experience',
  location: 'Location'
} as const;

type FilterKey = keyof typeof filterToLabel;

const allFilters = Object.keys(filterToLabel) as FilterKey[];

export default function CandidateSourcing({ searchObj }: { searchObj: ICandidateSourcingSearch }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [CandidatesState, dispatch] = useReducer(CandidatesReducer, InitialCandidatesState);
  const [searchObject, setSearchObject] = useState(searchObj);
  const [candidatesCount, setCandidatesCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [optionsCleared, setOptionsCleared] = useState(false);
  const [isJobDialogOpen, setIsJobDialogOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<IJob | undefined>(searchObject.job);

  const filters = (searchObject.filters || {}) as ICandidateSourcingFilters;
  const filterKeys = Object.keys(filters) as FilterKey[];
  const filterOptions = optionsCleared ? {} : searchObject.filter_options || {};

  const setErrorSnackbar = (message: string) => {
    dispatch({
      type: 'SET_SNACKBAR',
      payload: { message, state: 'error' }
    });
  };

  const { mutate: updateSearch } = useMutation({
    mutationFn: async ({ query, jobId }: { query: string, jobId?: number }) => {
      const response = await Api.updateSearch(searchObj.id, query, jobId);
      setSearchObject(response.res as ICandidateSourcingSearch);
      return response;
    }
  });

  const { mutate: executeSearch, isLoading: isExecutingSearch } = useMutation({
    mutationFn: async () => {
      dispatch({ type: 'SET_PAGE', payload: 1 });
      const response = await Api.executeSearch(searchObj.id, false);
      return response;
    },
    onSuccess: () => {
      refetchCandidates();
    },
    onError() {
      setErrorSnackbar('There was an error executing the search.');
    }
  });

  const { mutate: executeExpandedSearch, isLoading: isExecutingExpandedSearch } = useMutation({
    mutationFn: async () => {
      dispatch({ type: 'SET_PAGE', payload: 1 });
      const response = await Api.executeSearch(searchObj.id, true) as IApiResponse;
      if (response?.res?.search) {
        setSearchObject(response.res.search);
        setOptionsCleared(false);
      }
      return response;
    },
    onSuccess: () => {
      refetchCandidates();
    },
    onError() {
      setErrorSnackbar('There was an error executing the search.');
    }
  });

  const { mutate: updateFilters, isLoading: isUpdatingFilters } = useMutation({
    mutationFn: async (newFilters: ICandidateSourcingFilters) => {
      dispatch({ type: 'SET_PAGE', payload: 1 });
      const response = await Api.updateFilters(searchObj.id, newFilters);
      return response;
    },
    onSuccess: () => {
      refetchCandidates();
    },
    onError() {
      setErrorSnackbar('There was an error updating the filters.');
    }
  });

  const { mutate: removeSelectedCandidates } = useMutation({
    mutationFn: async (ids: number[]) => {
      const response = await Api.bulkRemoveCandidates(searchObj.id, ids);
      return response;
    },
    onSuccess: () => {
      refetchCandidates();
    },
    onError() {
      setErrorSnackbar('There was an error deleting the selected candidates.');
    }
  });

  const {
    data: candidates,
    isLoading: isFetchingCandidates,
    refetch: refetchCandidates
  } = useQuery({
    queryKey: [
      'candidates',
      searchObj.id,
      CandidatesState.candidatesTableSettings.page,
      CandidatesState.candidatesTableSettings.limit,
      CandidatesState.candidatesTableSettings.sort,
      CandidatesState.candidatesTableSettings.sort_order
    ],
    queryFn: async () => {
      const candidatesResponse = await Api.fetchCandidates(
        searchObj.id,
        CandidatesState.candidatesTableSettings.page,
        CandidatesState.candidatesTableSettings.limit,
        CandidatesState.candidatesTableSettings.sort,
        CandidatesState.candidatesTableSettings.sort_order
      ) as IApiResponse;
      setCandidatesCount(parseInt(candidatesResponse.resHead?.['x-total-count'] || '0'));
      setTotalPages(parseInt(candidatesResponse.resHead?.['x-total-pages'] || '0'));
      return candidatesResponse.res.candidates || [];
    }
  });

  const setQuery = (newQuery: string) => {
    updateSearch({
      query: newQuery,
      jobId: selectedJob?.id
    });
    setOptionsCleared(true);
  };

  const handleRemoveFilter = async (key: FilterKey) => {
    const newFilters = { ...filters };
    delete newFilters[key];
    setSearchObject({
      ...searchObject,
      filters: newFilters
    });
    await updateFilters(newFilters);
  };

  const handleUpdateSkill = async (
    skillGroupIndex: number,
    skillIndex: number,
    newSkill: string
  ) => {
    const newSkills = [...(filters.skills || [])];
    newSkills[skillGroupIndex][skillIndex] = newSkill;
    setSearchObject({
      ...searchObject,
      filters: { ...filters, skills: newSkills }
    });
    await updateFilters({ ...filters, skills: newSkills });
  };

  const handleUpdateJobTitle = async (index: number, newJobTitle: string) => {
    const newJobTitles = [...(filters.job_titles || [])];
    newJobTitles[index] = newJobTitle;
    setSearchObject({
      ...searchObject,
      filters: { ...filters, job_titles: newJobTitles }
    });
    await updateFilters({ ...filters, job_titles: newJobTitles });
  };

  const handleUpdateIndustry = async (index: number, newIndustry: string) => {
    const newIndustries = [...(filters.industries || [])];
    newIndustries[index] = newIndustry;
    setSearchObject({
      ...searchObject,
      filters: { ...filters, industries: newIndustries }
    });
    await updateFilters({ ...filters, industries: newIndustries });
  };

  const handleRemoveSkill = async (skillGroupIndex: number, skillIndex: number) => {
    const newSkills = [...(filters.skills || [])];
    newSkills[skillGroupIndex] = newSkills[skillGroupIndex].filter(
      (_, index) => index !== skillIndex
    );
    if (newSkills[skillGroupIndex].length === 0) {
      newSkills.splice(skillGroupIndex, 1);
    }
    setSearchObject({
      ...searchObject,
      filters: { ...filters, skills: newSkills }
    });
    await updateFilters({ ...filters, skills: newSkills });
  };

  const handleRemoveJobTitle = async (jobTitle: string) => {
    const newJobTitles = filters.job_titles?.filter((title) => title !== jobTitle) || [];
    setSearchObject({
      ...searchObject,
      filters: { ...filters, job_titles: newJobTitles }
    });
    await updateFilters({ ...filters, job_titles: newJobTitles });
  };

  const handleRemoveIndustry = async (industry: string) => {
    const newIndustries = filters.industries?.filter((i: string) => i !== industry) || [];
    setSearchObject({
      ...searchObject,
      filters: { ...filters, industries: newIndustries }
    });
    await updateFilters({ ...filters, industries: newIndustries });
  };

  const handleAddJobTitle = async () => {
    const newJobTitles = [...(filters.job_titles || []), ''];
    setSearchObject({
      ...searchObject,
      filters: { ...filters, job_titles: newJobTitles }
    });
  };

  const handleAddSkill = async () => {
    const newSkills = [...(filters.skills || [])];
    newSkills[newSkills.length - 1].push('');

    setSearchObject({
      ...searchObject,
      filters: { ...filters, skills: newSkills }
    });
  };

  const handleAddIndustry = async () => {
    const newIndustries = [...(filters.industries || []), ''];
    setSearchObject({
      ...searchObject,
      filters: { ...filters, industries: newIndustries }
    });
  };

  const doSearch = async () => {
    await executeSearch();
    await executeExpandedSearch();
  };

  const handleJobSelect = (job: IJob | undefined) => {
    setSelectedJob(job);
    updateSearch({
      query: searchObject.query,
      jobId: job?.id
    });
  };

  const isFilterActive = (filterKey: FilterKey) => {
    const value = filters[filterKey];
    if (value === undefined || value === null) return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  };

  return (
    <Stack sx={styles.candidatesPageContainer} ref={containerRef}>
      <Stack sx={styles.candidatesPageHeader} direction="row" justifyContent="space-between">
        Candidate Sourcing
        <Button onClick={() => setIsJobDialogOpen(true)} sx={baseSecondaryButtonStyles}>
          Job Description
        </Button>
      </Stack>
      <Stack direction="row" spacing={2}>
        <Search
          idLabel="candidates"
          placeholder="Enter a query"
          setSearch={(newQuery) => setQuery(newQuery)}
          initialValue={searchObject.query || ''}
        />
        <Button
          onClick={doSearch}
          sx={{ ...tableSearchButtonStyles, marginTop: '-5px !important', padding: '0 20px' }}
          disabled={
            isExecutingSearch ||
            isExecutingExpandedSearch ||
            isUpdatingFilters ||
            isFetchingCandidates
          }
        >
          Search
        </Button>
      </Stack>
      <Stack direction="row" spacing={2} sx={{ flexWrap: 'wrap', gap: 1 }}>
        {allFilters.map((filterKey) => {
          const active = isFilterActive(filterKey);
          const hasOptions = filterOptions[filterKey]?.length > 0;

          return (
            <Tag
              key={filterKey}
              label={filterToLabel[filterKey]}
              backgroundColor={active ? '#E3F2FD' : '#F5F5F5'}
              color={active ? '#1976D2' : '#757575'}
              isInteractive={active && hasOptions}
              options={['Add']}
              onSelect={(option) => {
                if (option === 'Add') {
                  if (filterKey === 'job_titles') {
                    handleAddJobTitle();
                  } else if (filterKey === 'skills') {
                    handleAddSkill();
                  } else if (filterKey === 'industries') {
                    handleAddIndustry();
                  }
                }
              }}
            />
          );
        })}
      </Stack>
      <Stack>
        <h3>Are these filters looking good?</h3>
        <Typography
          variant="body1"
          sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}
        >
          <strong>Looking for</strong>
          {filters.job_titles && filters.job_titles.length > 0 ? (
            <>
              {filters.job_titles.map((title, index) => (
                <React.Fragment key={title}>
                  <Tag
                    label={title}
                    onDelete={() => handleRemoveJobTitle(title)}
                    backgroundColor="#E3F2FD"
                    color="#1976D2"
                    options={filterOptions.job_titles || []}
                    onSelect={(option) => handleUpdateJobTitle(index, option)}
                    isInteractive={filterOptions.job_titles && filterOptions.job_titles.length > 0}
                    disabled={isUpdatingFilters}
                  />
                  {index < filters.job_titles!.length - 1 ? ' or ' : ''}
                </React.Fragment>
              ))}
            </>
          ) : (
            'candidates'
          )}
          {filters.skills && filters.skills.length > 0 && (
            <>
              <strong>skilled in</strong>
              {filters.skills.map((skillGroup, groupIndex) => (
                <React.Fragment key={groupIndex}>
                  {groupIndex > 0 && ' AND '}
                  {skillGroup.map((skill, skillIndex) => (
                    <React.Fragment key={skill}>
                      <Tag
                        label={skill}
                        onDelete={() => handleRemoveSkill(groupIndex, skillIndex)}
                        backgroundColor="#E3F2FD"
                        color="#1976D2"
                        options={filterOptions.skills || []}
                        onSelect={(option) => handleUpdateSkill(groupIndex, skillIndex, option)}
                        isInteractive={filterOptions.skills && filterOptions.skills.length > 0}
                        disabled={isUpdatingFilters}
                      />
                      {skillIndex < skillGroup.length - 1 ? ' or ' : ''}
                    </React.Fragment>
                  ))}
                </React.Fragment>
              ))}
            </>
          )}
          {filters.industries && filters.industries.length > 0 && (
            <>
              <strong> working in </strong>
              {filters.industries.map((industry, index) => (
                <React.Fragment key={industry}>
                  <Tag
                    label={industry}
                    onDelete={() => handleRemoveIndustry(industry)}
                    backgroundColor="#E3F2FD"
                    color="#1976D2"
                    options={filterOptions.industries || []}
                    onSelect={(option) => handleUpdateIndustry(index, option)}
                    isInteractive={filterOptions.industries && filterOptions.industries.length > 0}
                    disabled={isUpdatingFilters}
                  />
                  {index < filters.industries!.length - 1 ? ' or ' : ''}
                </React.Fragment>
              ))}
            </>
          )}
          {filters.years_of_experience && (
            <>
              <strong>with</strong>
              <Tag
                label={`${filters.years_of_experience} years of experience`}
                onDelete={() => handleRemoveFilter('years_of_experience')}
                backgroundColor="#E3F2FD"
                color="#1976D2"
                disabled={isUpdatingFilters}
              />
            </>
          )}
          {filters.location && (
            <>
              <strong>located in</strong>
              <Tag
                label={filters.location}
                onDelete={() => handleRemoveFilter('location')}
                backgroundColor="#E3F2FD"
                color="#1976D2"
                disabled={isUpdatingFilters}
              />
            </>
          )}
        </Typography>
      </Stack>
      <Stack>
        <em>
          Found <strong className="px-2">{candidatesCount}</strong> candidates
        </em>
        <Button
          onClick={() => {
            location.href = `/admin/candidate_sourcing/searches/${searchObj.id}/screening`;
          }}
          sx={{ ...tableSearchButtonStyles, width: '146px', height: '35px', marginTop: '10px' }}
        >
          Screen Candidates
        </Button>
      </Stack>
      {isExecutingExpandedSearch && (
        <Stack>
          <em>
            Cannot find what you are looking for? Do not worry, we are expanding your search to find
            more candidates.
          </em>
        </Stack>
      )}
      <GenericTable
        label="candidates list"
        items={candidates || []}
        isLoadingItems={
          isFetchingCandidates ||
          isExecutingSearch ||
          (isExecutingExpandedSearch && candidatesCount > 0)
        }
        tableState={CandidatesState.candidatesTableState}
        dispatch={dispatch}
        headerStyle={headerStyle}
        paginationState={CandidatesState.candidatesTableSettings}
        pagination={{
          totalItems: candidatesCount,
          totalPages
        }}
        TableRows={CandidatesTableRows}
        TableActionList={undefined}
        allowMultipleSelection={candidatesCount ? true : false}
        titleCellId="id"
        userPreferenceSource="candidates"
      />
      <TotalSelectedIndicatorWithActions
        totalSelected={CandidatesState.candidatesTableState.selectedItemIds.length}
        parentDiv={containerRef.current}
        dispatch={dispatch}
        actionButtonId="delete-candidate-action-button"
        actionButtonsList={[
          {
            id: 'delete',
            label: 'Delete',
            icon: <DeleteIcon />
          }
        ]}
        actionCall={() => {
          removeSelectedCandidates(CandidatesState.candidatesTableState.selectedItemIds);
          dispatch({
            type: 'REMOVE_ALL_PAGE_ITEMS',
            payload: CandidatesState.candidatesTableState.selectedItemIds
          });
        }}
      />
      <JobSelectionDialog
        selectedJob={selectedJob}
        setSelectedJob={setSelectedJob}
        isOpen={isJobDialogOpen}
        onClose={() => setIsJobDialogOpen(false)}
        onUpdate={handleJobSelect}
      />
    </Stack>
  );
}
